import React from "react";
import { Button } from "@/components/ui/button";
import { Bell, BrainCircuit, ScrollText, ShieldCheck, ArrowRight } from "lucide-react";
import { HID } from "@/constants/cosecIds";

const FEATURES = [
  { Icon: Bell, title: "Automated Reminders", desc: "Never miss a filing deadline, automatic alerts for every entity." },
  { Icon: BrainCircuit, title: "AI Compliance Workflows", desc: "Intelligent automation that drafts filings, surfaces risks, and recommends actions." },
  { Icon: ScrollText, title: "Secure Audit Trail", desc: "Time-stamped, immutable history of every governance action and update." },
  { Icon: ShieldCheck, title: "GDPR Ready", desc: "Data residency, retention controls and consent management out of the box." },
];

export default function Compliance() {
  return (
    <section id="security" className="py-20 md:py-28 bg-secondary/40 border-y border-border">
      <div className="container-cv">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <span className="section-eyebrow">UK regulations · ECCTA · Companies House</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.08]">
              Stay Compliant with UK Regulations and{" "}
              <span className="text-accent">Filing Deadlines.</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
              Businesses must keep up with changing company law, tax rules, and data
              protection requirements. Missing deadlines or inaccurate records can lead
              to penalties and delays.
            </p>
            <p className="mt-3 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
              CoSec helps you stay compliant with automated reminders, secure
              record-keeping, audit trails, and accurate filings, all in one platform.
            </p>
            <Button
              asChild
              data-testid={HID.ctaCompliance}
              className="mt-7 h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
            >
              <a href="/contact">
                See How It Works <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>

          <div className="lg:col-span-6">
            <div className="grid sm:grid-cols-2 gap-4">
              {FEATURES.map(({ Icon, title, desc }, i) => (
                <div
                  key={i}
                  className="group rounded-2xl border border-border bg-white p-6 hover:-translate-y-1 hover:border-accent/60 hover:shadow-md transition-all"
                >
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-primary/10 text-primary group-hover:bg-accent group-hover:text-primary transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold text-primary tracking-tight">{title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
